import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Coins, Crown, TrendingUp, AlertTriangle, ShoppingCart } from 'lucide-react';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { useUserRole } from '@/hooks/useUserRole';
import { Link } from 'react-router-dom';
import { usePricingRates } from '@/hooks/usePricingRates';
import { creditsToMinutes } from '@/utils/creditConversionUtils';
import { getCreditWarningLevel, getCreditWarningMessage } from '@/utils/creditWarnings';
import { cn } from '@/lib/utils';

export const CreditBalanceCard = () => {
  const { credits, totalAvailableCredits, monthlyFreeAvailable, isLoading, isLowCredits, isOutOfCredits } = usePractitionerCredits();
  const { audioRate, tier } = usePricingRates();
  const { hasPractitionerAccess } = useUserRole();

  // Calculate credit warning level and message
  const warningLevel = getCreditWarningLevel(totalAvailableCredits);
  const userType = hasPractitionerAccess ? 'practitioner' : 'individual';
  const warningInfo = getCreditWarningMessage(totalAvailableCredits, userType);

  if (isLoading) {
    return (
      <Card className="bg-gradient-to-br from-primary/5 via-background to-background border-primary/20">
        <CardContent className="p-6">
          <div className="animate-pulse flex items-center justify-between">
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded w-24"></div>
              <div className="h-8 bg-muted rounded w-32"></div>
            </div>
            <div className="h-12 w-12 bg-muted rounded-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!credits) {
    return null; // Don't show card if no credit data
  }

  return (
    <Card className={cn(
      "bg-gradient-to-br from-primary/5 via-background to-background shadow-lg hover:shadow-xl transition-shadow",
      // Add warning border colors
      warningLevel === 'critical'
        ? 'border-destructive/50 border-2'
        : warningLevel === 'low'
        ? 'border-orange-500/50 border-2'
        : 'border-primary/20'
    )}>
      <CardContent className="p-6">
        {/* Warning alert for low/critical states */}
        {warningInfo && (
          <div className={cn(
            "mb-4 p-3 rounded-md flex items-start gap-2",
            warningLevel === 'critical'
              ? 'bg-destructive/10 border border-destructive/20'
              : 'bg-orange-500/10 border border-orange-500/20'
          )}>
            <AlertTriangle className={cn(
              "h-5 w-5 flex-shrink-0 mt-0.5",
              warningLevel === 'critical' ? 'text-destructive' : 'text-orange-500'
            )} />
            <div className="flex-1">
              <p className={cn(
                "font-semibold text-sm",
                warningLevel === 'critical' ? 'text-destructive' : 'text-orange-700'
              )}>
                {warningInfo.title}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{warningInfo.message}</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          {/* Left side - Credit balance */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-muted-foreground">Your Credits</p>
              {isOutOfCredits && (
                <Badge variant="destructive" className="text-xs">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Out of Credits
                </Badge>
              )}
              {isLowCredits && !isOutOfCredits && (
                <Badge variant="outline" className="text-xs border-yellow-500 text-yellow-700">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Running Low
                </Badge>
              )}
            </div>
            <div className="flex items-baseline gap-3">
              <div className="text-4xl font-bold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                {totalAvailableCredits}
              </div>
              <div className="flex flex-col">
                <Coins className="h-8 w-8 text-primary opacity-80" />
                {audioRate > 0 && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    ~{creditsToMinutes(totalAvailableCredits, audioRate)} min
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              {credits.credits_balance || 0} purchased + {monthlyFreeAvailable} bonus
            </p>
          </div>

          {/* Right side - Quick actions */}
          <div className="flex flex-col gap-2">
            <Button
              asChild
              size="sm"
              variant="default"
              className="bg-gradient-to-r from-primary to-primary-glow hover:from-primary-glow hover:to-primary"
            >
              <Link to="/preferences?tab=credits">
                <ShoppingCart className="h-4 w-4 mr-2" />
                Purchase Credits
              </Link>
            </Button>
            <Button
              asChild
              size="sm"
              variant="outline"
              className="border-primary/20"
            >
              <Link to="/preferences?tab=credits">
                <Crown className="h-4 w-4 mr-2" />
                Upgrade Plan
              </Link>
            </Button>
          </div>
        </div>

        {/* Usage tip */}
        <div className="mt-4 pt-4 border-t border-border/50">
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <span className="inline-block w-2 h-2 bg-primary rounded-full"></span>
            {audioRate > 0
              ? `Your rate: ${audioRate} credits/minute (${tier} tier)`
              : 'Personalized pricing based on your tier'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
